import React, { useEffect, useState } from 'react';

import { gql } from '@apollo/client';
import { useMutation } from '@apollo/react-hooks';
import { useHistory } from 'react-router-dom';

import * as actions from 'actions';
import Form from 'Components-redesign/Form';
import { useIntegrationsConfigsUniverse } from 'Components-redesign/IntegrationsConfigsUniverse';
import { useDispatch, useFormData, useI18n, useSelector } from 'Hooks';
import {
  getCompany_company_categories as Category,
  getCompany_company_courseTypes as CourseType,
} from 'Scenes-redesign/PIM/__queries__';
import {
  ActivatedFeatures,
  areFormValuesEqual,
  getCategoriesList,
  getCoursetypeList,
} from 'Scenes-redesign/PIM/utils';
import { getLangText } from 'utils/langText';

import {
  createCategory,
  createCategoryVariables,
  updateCategory,
  updateCategoryMetadata,
  updateCategoryMetadataVariables,
  updateCategoryVariables,
} from './__queries__';
import { getCategoryFormFields } from './fields';
import { CREATE_MUTATION, NODE_QUERY, UPDATE_MUTATION } from './queries';
import { showQuestionModal } from './utils';

const UPDATE_UK_VAT = gql`
  mutation updateCategoryMetadata($input: UpdateCategoryMetadataInput!) {
    node: updateCategoryMetadata(input: $input) {
      success
      error
    }
  }
`;

interface Props {
  idToUpdate?: string;
  categories?: Category[];
  coursetypes: CourseType[];
  onFormSuccess: () => void;
  activatedFeatures: ActivatedFeatures;
  setShowUnsavedChangesQuestionModal?: React.Dispatch<React.SetStateAction<boolean>>;
}

const CategoryForm = ({
  idToUpdate,
  categories,
  coursetypes,
  onFormSuccess,
  activatedFeatures,
  setShowUnsavedChangesQuestionModal,
}: Props) => {
  const [changes, setChanges] = useState<any>(null);
  const { t } = useI18n();
  const history = useHistory();
  const dispatch = useDispatch();
  const integrationFields = useSelector((state) => state.integrationFields);
  const configurationsData = useIntegrationsConfigsUniverse();
  const currency = useSelector((state) => state.companyData?.defaultCurrency);
  const [updateUkVat] = useMutation<updateCategoryMetadata, updateCategoryMetadataVariables>(
    UPDATE_UK_VAT,
  );

  const categoryFields = integrationFields.filter((field) => field.model === 'CATEGORY');

  const categoriesList = getCategoriesList(categories, idToUpdate);

  const coursetypeList = getCoursetypeList(coursetypes);

  const defaultFields = getCategoryFormFields(
    t,
    idToUpdate,
    activatedFeatures,
    categoriesList,
    coursetypeList,
    currency === 'GBP' || currency === 'NOK',
  );

  const [create] = useMutation<createCategory, createCategoryVariables>(CREATE_MUTATION);
  const [update] = useMutation<updateCategory, updateCategoryVariables>(UPDATE_MUTATION);
  const [formState, getMutationData] = useFormData(idToUpdate, defaultFields, NODE_QUERY);

  useEffect(() => {
    const categoryFieldIndex = formState.findIndex((field) => field.id == 'parentId');
    if (categoryFieldIndex == -1) return;
    formState[categoryFieldIndex].list = categoriesList;
  }, [categories]);

  useEffect(() => {
    if (idToUpdate && setShowUnsavedChangesQuestionModal) {
      if (formState && changes && areFormValuesEqual(formState, changes)) {
        setShowUnsavedChangesQuestionModal(false);
      } else {
        setShowUnsavedChangesQuestionModal(true);
      }
    }
  }, [idToUpdate, formState, changes]);

  const handleFormSuccess = (node: any) => {
    if (!changes?.ukVatRate?.value) {
      onFormSuccess();
      return;
    }
    updateUkVat({
      variables: {
        input: {
          categoryId: node.id,
          companyId: node.company.id,
          ukVatRate: parseInt(changes?.ukVatRate?.value),
        },
      },
    });

    onFormSuccess();
    // When creating a category, if there are integrations, open the created category in edit mode
    if (!idToUpdate && (categoryFields.length || configurationsData.configs.length)) {
      history.push('/pim/categories/' + node.id);
      setTimeout(() => {
        dispatch(actions.showNotice(t('PIM.SetUpIntegrationAccountNumbers'), 5000));
      }, 5000);
    }
  };

  return (
    <div>
      <Form
        idToUpdate={idToUpdate}
        fields={formState}
        createMutation={create}
        updateMutation={update}
        parseMutationVariables={getMutationData}
        withDataFormat={false}
        buttonText={idToUpdate ? t('PIM.UpdateCategory') : t('PIM.CreateCategory')}
        floatingButton={true}
        onFormSuccess={handleFormSuccess}
        integrationModal={'CATEGORY'}
        getFieldChanges={(values) => setChanges(values)}
        handleQuestionModal={() => showQuestionModal(changes, categoriesList)}
        questionBeforeSubmit={`${t('PIM.AlreadyCategoryWithName')} ${getLangText(
          changes?.nameLang?.value,
        )}, ${t('PIM.SureWantToContinue')}`}
      />
    </div>
  );
};

export default CategoryForm;
